import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

const PRINCIPLES = [
  { t: "Precise, not promotional", d: "We describe what the engineering architecture does and where authority sits — without overclaiming." },
  { t: "Evidence at source", d: "Private data stays with its custodian. KATLAS records the governed action, not the data." },
  { t: "Reusable by design", d: "One coherent governance substrate across sectors, sandboxes and licences." },
];

function Field({ label, name, type = "text", textarea = false, value, onChange }) {
  const cls =
    "mt-2 w-full border border-ink/20 bg-transparent px-4 py-3 font-sans text-sm text-ink placeholder:text-ink/35 outline-none transition-colors focus:border-teal";
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/50">{label}</span>
      {textarea ? (
        <textarea name={name} rows={5} value={value} onChange={onChange} className={cls} data-testid={`contact-${name}`} />
      ) : (
        <input name={name} type={type} value={value} onChange={onChange} className={cls} data-testid={`contact-${name}`} />
      )}
    </label>
  );
}

export default function Company() {
  const [form, setForm] = useState({ name: "", org: "", email: "", journey: "" });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.journey) {
      toast.error("Please add your name, email and a short journey outline.");
      return;
    }
    toast.success("Noted — we will be in touch about your journey.");
    setForm({ name: "", org: "", email: "", journey: "" });
  };

  return (
    <div data-testid="company-page">
      <PageIntro
        index="/company"
        eyebrow="Company"
        titleLines={["Operational governance,", "built to be executable", "and reusable."]}
        lead="KATLAS Technology builds runtime operational-governance infrastructure. The Cierge Design Studio is our applied studio. Together they turn institutional decision boundaries into proof."
        meta={["About", "Context", "Contact"]}
      />

      {/* About */}
      <section className="border-b border-ink/12 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 01</span>
            <h2 className="mt-4 font-serif text-2xl leading-snug text-ink md:text-3xl">About</h2>
          </div>
          <div className="md:col-span-8">
            <p className="max-w-2xl font-sans text-lg leading-relaxed text-ink/75">
              We make the point where authority meets action executable, verifiable
              and reusable. Institutional policy and authorised roles decide; KATLAS
              attests the governed handoff and signs the receipt. The website
              explains the engineering architecture, the Cierge Design Studio shapes and proves
              the journey, and receipts evidence what happened.
            </p>
            <RevealGroup className="mt-12 grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 sm:grid-cols-3">
              {PRINCIPLES.map((p) => (
                <RevealItem key={p.t} className="bg-stone p-7">
                  <h3 className="font-serif text-lg text-ink">{p.t}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-ink/70">{p.d}</p>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </div>
      </section>

      {/* Context */}
      <section className="border-b border-ink/12 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 02</span>
            <h2 className="mt-4 font-serif text-2xl leading-snug text-ink md:text-3xl">Company context</h2>
          </div>
          <div className="md:col-span-8">
            <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
              KATLAS Technology is the company and the underlying governance
              technology. The KATLAS engineering architecture is the reusable runtime substrate.
              The Cierge Design Studio is the applied journey discovery, design
              and pilot capability that proves governed journeys with sponsors —
              always beginning with a single consequential handoff.
            </p>
            <blockquote className="mt-10 border-l-2 border-teal pl-6">
              <p className="font-serif text-xl italic leading-snug text-ink md:text-2xl">
                Institutional policy and authorised roles decide. KATLAS makes the
                decision boundary executable, verifiable and reusable.
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="scroll-mt-28 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-5">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ 03</span>
            <h2 className="mt-4 font-serif text-3xl leading-snug tracking-tight text-ink md:text-4xl">
              Bring us a journey
            </h2>
            <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-ink/70 md:text-lg">
              Tell us about one consequential handoff and the role that owns the
              outcome. We will shape it into a governed journey with a clear
              custody boundary, authority matrix and receipt model.
            </p>
            <div className="mt-8 space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">Direct</p>
              <a href="mailto:hello@katlastechnology.io" className="font-mono text-sm text-ink underline decoration-teal/50 underline-offset-4 hover:text-teal" data-testid="contact-email-link">
                hello@katlastechnology.io
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={onSubmit} className="border border-ink/15 p-8 md:p-10" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label="Name" name="name" value={form.name} onChange={onChange} />
                <Field label="Organisation" name="org" value={form.org} onChange={onChange} />
              </div>
              <div className="mt-6">
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} />
              </div>
              <div className="mt-6">
                <Field label="Outline the journey" name="journey" textarea value={form.journey} onChange={onChange} />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                className="group mt-8 inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
              >
                Send outline
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
              </button>
              <p className="mt-4 font-mono text-[11px] text-ink/40">
                This form is not yet wired to a backend inbox in this build.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
